import { useCallback, useEffect, useMemo, useState } from 'react';
import useTableState from '../../hooks/useTableState';
import { Box, Button } from '@mui/material';
import { MaterialReactTable, useMaterialReactTable } from 'material-react-table';
import { DownloadIcon } from '@phosphor-icons/react';
import { download, generateCsv, mkConfig } from 'export-to-csv';
import { getAllCustomers } from '../../api/customerApi';
import { getAllSales, getAllSalesByAgentId } from '../../api/salesApi';
import { useUser } from '../../context/userContext';
import handleAgentName from '../../utils/userUtils';
import ViewCustomerModal from './viewCustomerModal';
import { formatPhoneNumber } from '../../utils/phoneUtils';

export default function CustomerList() {
    const ts = useTableState();
  const { user } = useUser();
  const [customers, setCustomers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [viewModal, setViewModal] = useState({ open: false, customerId: null });

  const isAdmin = user?.roleRights?.includes('AllSalesList');

  const fetchData = useCallback(async () => {
    try {
      // Get sales to map customers to agents
      const salesRes = isAdmin
        ? await getAllSales()
        : await getAllSalesByAgentId(user.userId);

      const salesData = salesRes.data;

      // Build customer map from sales — each customer gets their sales agent and co-agent
      const customerMap = {};
      salesData.forEach(sale => {
        const cId = sale.customerDetails?.customerName; // use name as key since customerId may not be in sale response directly
        if (!customerMap[cId]) {
          customerMap[cId] = {
            customerName: sale.customerDetails?.customerName ?? '',
            email: sale.customerDetails?.email ?? '',
            phoneNumber: sale.customerDetails?.phoneNumber ?? '',
            address: sale.customerDetails?.address ?? '',
            salesAgent: sale.agentDetails ? `${sale.agentDetails.groupName ?? ''} - ${handleAgentName(sale.agentDetails.firstName, sale.agentDetails.lastName, sale.agentDetails.alias)}`.trim() : '',
            coAgent: sale.coAgentDetails ? `${sale.coAgentDetails.groupName ?? ''} - ${handleAgentName(sale.coAgentDetails.firstName, sale.coAgentDetails.lastName, sale.coAgentDetails.alias)}`.trim() : '',
            salesCount: 0,
            customerId: sale.customerId,
          };
        }
        customerMap[cId].salesCount += 1;
      });

      // Also fetch standalone customers (those without sales)
      if (isAdmin) {
        try {
          const custRes = await getAllCustomers();
          custRes.data.forEach(c => {
            if (!customerMap[c.customerName]) {
              customerMap[c.customerName] = {
                ...c,
                salesAgent: '',
                coAgent: '',
                salesCount: 0,
              };
            } else {
              // Merge customerId from customer table
              customerMap[c.customerName].customerId = c.customerId;
              customerMap[c.customerName].email = c.email || customerMap[c.customerName].email;
              customerMap[c.customerName].phoneNumber = c.phoneNumber || customerMap[c.customerName].phoneNumber;
              customerMap[c.customerName].address = c.address || customerMap[c.customerName].address;
            }
          });
        } catch (e) { /* ignore if customer API fails */ }
      }

      setCustomers(Object.values(customerMap));
    } catch (error) {
      console.error('Error fetching customers:', error);
    } finally {
      setLoading(false);
    }
  }, [isAdmin, user?.userId]);

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  const columns = useMemo(() => [
    { id: 'customerName', header: 'Customer Name', accessorKey: 'customerName', size: 160 },
    { id: 'phoneNumber', header: 'Phone', accessorFn: row => formatPhoneNumber(row.phoneNumber), size: 140 },
    { id: 'email', header: 'Email', accessorFn: row => row.email ?? '—', size: 150 },
    {
      id: 'address', header: 'Address', accessorKey: 'address', size: 180,
      Cell: ({ cell }) => (
        <span style={{ maxWidth: 180, display: 'inline-block', whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>
          {cell.getValue() || '—'}
        </span>
      ),
    },
    { id: 'salesAgent', header: 'Sales Agent', accessorKey: 'salesAgent', size: 150 },
    { id: 'coAgent', header: 'Co-Agent (Offline)', accessorFn: row => row.coAgent || '—', size: 150 },
    {
      id: 'actions', header: '', size: 80, enableSorting: false, enableColumnFilter: false,
      Cell: ({ row }) => (
        <Box sx={{ display: 'flex', gap: 1.5 }}>
          <span onClick={() => setViewModal({ open: true, customerId: row.original.customerId })} style={{ color: '#1A8CFF', cursor: 'pointer', fontWeight: 500, fontSize: '0.8rem' }}>View</span>
        </Box>
      ),
    },
  ], []);

  const csvConfig = mkConfig({ filename: 'Customer_Data_Export', fieldSeparator: ',', decimalSeparator: '.', useKeysAsHeaders: true });

  const handleExportData = () => {
    const exportData = customers.map(c => ({
      'Customer Name': c.customerName,
      'Phone': c.phoneNumber,
      'Email': c.email ?? '',
      'Address': c.address ?? '',
      'Sales Agent': c.salesAgent,
      'Co-Agent (Offline)': c.coAgent,
    }));
    const csv = generateCsv(csvConfig)(exportData);
    download(csvConfig)(csv);
  };

  const table = useMaterialReactTable({
    columns, data: customers,
    enableColumnActions: false, enableColumnDragging: false,
    enableDensityToggle: false, enableFullScreenToggle: false, enableHiding: false,
    enableStickyHeader: true, muiTableContainerProps: { sx: { maxHeight: 'unset' } },
    initialState: { density: 'compact', showGlobalFilter: true },
    state: { globalFilter: ts.globalFilter, sorting: ts.sorting, pagination: ts.pagination, columnFilters: ts.columnFilters },
    onGlobalFilterChange: ts.setGlobalFilter,
    onSortingChange: ts.setSorting,
    onPaginationChange: ts.setPagination,
    onColumnFiltersChange: ts.setColumnFilters,
    muiTablePaperProps: { elevation: 0, sx: { border: 'none', backgroundColor: 'transparent', borderRadius: '14px' } },
    muiTableHeadCellProps: { sx: { backgroundColor: '#F8FAFC', borderBottom: '2px solid #E2E8F0', textTransform: 'uppercase', fontSize: '0.68rem', fontWeight: 600, letterSpacing: '0.5px', color: '#64748B', fontFamily: "'Poppins', sans-serif", padding: '8px 10px' } },
    muiTableBodyCellProps: { sx: { fontSize: '0.8rem', color: '#374151', borderBottom: '1px solid #F1F5F9', fontFamily: "'Poppins', sans-serif", padding: '6px 10px' } },
    muiTableBodyRowProps: ({ row }) => ({
      onClick: (e) => {
        if (e.target.closest('input[type="checkbox"]') || (e.target.tagName === 'SPAN' && e.target.style.cursor === 'pointer')) return;
        setViewModal({ open: true, customerId: row.original.customerId });
      },
      sx: { cursor: 'pointer', '&:hover': { backgroundColor: '#F0F7FF', transition: 'background-color 0.12s' } },
    }),
    muiTopToolbarProps: { sx: { backgroundColor: '#FFFFFF', borderBottom: '1px solid #E2E8F0', minHeight: '48px' } },
    muiBottomToolbarProps: { sx: { backgroundColor: '#FFFFFF', borderTop: '1px solid #E2E8F0' } },
    muiSearchTextFieldProps: { placeholder: 'Search customers...', sx: { '& .MuiOutlinedInput-root': { borderRadius: '8px', backgroundColor: '#F8FAFC', fontSize: '0.8rem', fontFamily: "'Poppins', sans-serif" } } },
    muiPaginationProps: { shape: 'rounded', showRowsPerPage: true, rowsPerPageOptions: [10, 25, 50, 100] },
    renderTopToolbarCustomActions: () => (
      <Box sx={{ display: 'flex', gap: '8px', alignItems: 'center' }}>
        <button onClick={handleExportData} style={{ border: '1px solid #E2E8F0', background: 'white', borderRadius: '7px', padding: '6px 14px', fontSize: '0.775rem', fontWeight: 500, color: '#374151', cursor: 'pointer', fontFamily: "'Poppins', sans-serif", display: 'flex', alignItems: 'center', gap: '6px' }}>
          <DownloadIcon size={14} /> Export All
        </button>
      </Box>
    ),
  });

  if (loading) return <div>Loading...</div>;

  return (
    <div>
      <Box sx={{ mb: 2 }}>
        <h2 style={{ margin: 0, fontSize: '1.25rem', fontWeight: 700, color: '#0F172A', fontFamily: "'Poppins', sans-serif" }}>All Customer</h2>
        <p style={{ margin: 0, fontSize: '0.8rem', color: '#64748B', fontFamily: "'Poppins', sans-serif" }}>
          {isAdmin ? 'View and manage all customers' : 'View your customers'}
        </p>
      </Box>
      <div style={{ backgroundColor: '#fff', borderRadius: 14, boxShadow: '0 1px 3px rgba(0,0,0,0.08)' }}>
        <MaterialReactTable table={table} />
      </div>
      <ViewCustomerModal open={viewModal.open} customerId={viewModal.customerId} onClose={() => setViewModal({ open: false, customerId: null })} />
    </div>
  );
}
